import { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { StatusOrb } from "./StatusOrb";
import { OrbPopover } from "./OrbPopover";
import type { FeedbackValue } from "./useFeedbackState";
import { toast } from "sonner";

export interface IssueData {
  id: string;
  name: string;
  subcomponent?: string;
  activeSince?: string;
  lastSeen?: string;
}

interface IssueCardProps {
  issue: IssueData;
  feedback: FeedbackValue;
  onFeedbackChange: (issueId: string, value: FeedbackValue) => void;
  isOnSite: boolean;
}

const cardStyles: Record<string, string> = {
  confirmed: "bg-[var(--orb-green-bg)] border-l-[var(--orb-green)]",
  "not-found": "opacity-70 border-l-[var(--orb-gray)]",
  "not-sure": "bg-[var(--orb-amber-bg)] border-l-[var(--orb-amber)]",
};

export function IssueCard({ issue, feedback, onFeedbackChange, isOnSite }: IssueCardProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement | null>(null);

  const handleOrbClick = (e: React.MouseEvent) => {
    containerRef.current = cardRef.current?.closest(".page-shell, .page-content, [data-card-container]") as HTMLElement;
    setPopoverOpen(true);
  };

  const handleSelect = (value: FeedbackValue) => {
    const prev = feedback;
    onFeedbackChange(issue.id, value);
    setPopoverOpen(false);
    // Only toast when setting (not clearing)
    if (prev !== value && value !== null) {
      toast("Synced to Site Visit Report", {
        className: "!bg-[#1F2937] !text-white !text-[13px] !font-medium !rounded-[20px] !px-5 !py-2.5",
        duration: 2000,
      });
    }
  };

  const hasFeedback = feedback !== null;

  return (
    <div
      ref={cardRef}
      className={cn(
        "bg-white border border-[var(--orb-border)] rounded-xl px-3.5 py-3 flex items-center gap-3 transition-all duration-300 border-l-[3px] relative",
        isOnSite ? "mb-2.5" : "mb-2",
        hasFeedback ? cardStyles[feedback!] : "border-l-transparent"
      )}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[15px] font-bold text-[var(--orb-text-primary)] truncate">
            {issue.name}
          </span>
          {issue.subcomponent && (
            <span className="text-[11px] font-semibold text-[var(--orb-purple)] bg-[var(--orb-purple-light)] px-2 py-0.5 rounded shrink-0">
              {issue.subcomponent}
            </span>
          )}
        </div>
        <div className="text-xs text-[var(--orb-text-tertiary)]">
          {issue.activeSince && <>Active {issue.activeSince}</>}
          {issue.activeSince && issue.lastSeen && <> · </>}
          {issue.lastSeen && <>Last seen {issue.lastSeen}</>}
        </div>
      </div>

      <StatusOrb
        feedback={feedback}
        isOnSite={isOnSite}
        onClick={handleOrbClick}
      />

      {popoverOpen && (
        <OrbPopover
          currentFeedback={feedback}
          onSelect={handleSelect}
          onClose={() => setPopoverOpen(false)}
          anchorRect={cardRef.current?.getBoundingClientRect() ?? null}
          containerRect={containerRef.current?.getBoundingClientRect() ?? null}
        />
      )}
    </div>
  );
}
